import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  AsYouType,
} from 'libphonenumber-js';

/**
 * Supported countries for Helpdesk user registration (ISO region + calling code).
 */
export const PHONE_COUNTRY_OPTIONS = [
  { region: 'KE', callingCode: '+254', label: '+254 (Kenya)' },
  { region: 'IN', callingCode: '+91', label: '+91 (India)' },
  { region: 'US', callingCode: '+1', label: '+1 (US/CA)' },
  { region: 'AE', callingCode: '+971', label: '+971 (UAE)' },
  { region: 'GB', callingCode: '+44', label: '+44 (UK)' },
  { region: 'AU', callingCode: '+61', label: '+61 (Australia)' },
  { region: 'DE', callingCode: '+49', label: '+49 (Germany)' },
  { region: 'FR', callingCode: '+33', label: '+33 (France)' },
  { region: 'JP', callingCode: '+81', label: '+81 (Japan)' },
  { region: 'CN', callingCode: '+86', label: '+86 (China)' },
  { region: 'SA', callingCode: '+966', label: '+966 (Saudi Arabia)' },
];

const DEFAULT_REGION = 'KE';

export function getCountryOptionByRegion(region) {
  return PHONE_COUNTRY_OPTIONS.find((c) => c.region === region)
    || PHONE_COUNTRY_OPTIONS.find((c) => c.region === DEFAULT_REGION);
}

export function getCountryOptionByCallingCode(callingCode) {
  const normalized = (callingCode || '').trim();
  return PHONE_COUNTRY_OPTIONS.find((c) => c.callingCode === normalized)
    || getCountryOptionByRegion(DEFAULT_REGION);
}

/**
 * Resolve ISO region from calling code (e.g. +254 -> KE) or return region if already ISO.
 */
export function resolveRegion(regionOrCallingCode) {
  if (!regionOrCallingCode) return DEFAULT_REGION;
  const value = String(regionOrCallingCode).trim().toUpperCase();
  if (value.length === 2 && /^[A-Z]{2}$/.test(value)) {
    return value;
  }
  return getCountryOptionByCallingCode(regionOrCallingCode).region;
}

/**
 * Validate national or international phone input for a region.
 */
export function validatePhoneInput(phoneInput, regionOrCallingCode) {
  const raw = (phoneInput || '').trim();
  if (!raw) {
    return { valid: false, message: 'Phone number is required' };
  }
  const region = resolveRegion(regionOrCallingCode);
  try {
    const valid = isValidPhoneNumber(raw, region)
      || (raw.startsWith('+') && isValidPhoneNumber(raw));
    if (!valid) {
      return { valid: false, message: 'Please enter a valid phone number for the selected country' };
    }
    return { valid: true, message: '' };
  } catch {
    return { valid: false, message: 'Please enter a valid phone number' };
  }
}

/**
 * Build API payload fields: national phone string + phoneRegion (ISO).
 */
export function buildPhoneApiFields(nationalInput, regionOrCallingCode) {
  const region = resolveRegion(regionOrCallingCode);
  const raw = (nationalInput || '').trim();
  if (!raw) {
    return { phone: '', phoneRegion: region };
  }
  const parsed = parsePhoneNumberFromString(raw, region)
    || (raw.startsWith('+') ? parsePhoneNumberFromString(raw) : null);
  if (!parsed || !parsed.isValid()) {
    return { phone: raw, phoneRegion: region };
  }
  return {
    phone: parsed.formatNational().replace(/\D/g, '') || String(parsed.nationalNumber),
    phoneRegion: parsed.country || region,
    phoneE164: parsed.format('E.164'),
  };
}

/**
 * Parse stored phone (E.164 or legacy) into form state.
 */
export function parsePhoneForForm(storedPhone, fallbackRegion = DEFAULT_REGION) {
  const full = (storedPhone || '').trim();
  if (!full) {
    return {
      region: fallbackRegion,
      callingCode: getCountryOptionByRegion(fallbackRegion).callingCode,
      nationalNumber: '',
      e164: '',
    };
  }

  try {
    const parsed = parsePhoneNumberFromString(full);
    if (parsed && parsed.isValid()) {
      const region = parsed.country || fallbackRegion;
      const option = getCountryOptionByRegion(region);
      return {
        region,
        callingCode: option.callingCode,
        nationalNumber: String(parsed.nationalNumber),
        e164: parsed.format('E.164'),
      };
    }
  } catch {
    // fall through to legacy parsing
  }

  const sorted = [...PHONE_COUNTRY_OPTIONS].sort(
    (a, b) => b.callingCode.length - a.callingCode.length
  );
  for (const option of sorted) {
    if (full.startsWith(option.callingCode)) {
      return {
        region: option.region,
        callingCode: option.callingCode,
        nationalNumber: full.substring(option.callingCode.length).replace(/\D/g, ''),
        e164: full,
      };
    }
  }

  return {
    region: fallbackRegion,
    callingCode: getCountryOptionByRegion(fallbackRegion).callingCode,
    nationalNumber: full.replace(/\D/g, ''),
    e164: full,
  };
}

export function formatPhoneAsYouType(value, regionOrCallingCode) {
  const region = resolveRegion(regionOrCallingCode);
  return new AsYouType(region).input(value || '');
}

export function formatPhoneDisplay(e164OrLegacy) {
  const full = (e164OrLegacy || '').trim();
  if (!full) return 'N/A';
  try {
    const parsed = parsePhoneNumberFromString(full);
    if (parsed && parsed.isValid()) {
      return parsed.formatInternational();
    }
  } catch {
    // ignore
  }
  return full;
}
